import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Calendar, Clock, Globe, Play, Star, Users } from 'lucide-react';
import VideoPlayer from '../../VideoPlayer';
import EpisodesList from '../../EpisodesList';
import WatchlistButton from '../../WatchlistButton';
import WatchedButton from '../../WatchedButton';
import AddToPlaylistButton from '../AddToPlaylistButton';
import CastRail from '../actors/CastRail';
import Rail from '../../ui/Rail';
import Poster from '../../ui/Poster';
import { useCatalog } from '../../providers/CatalogProvider';
import { similarTo } from '../../lib/catalog';
import { backdropSrc } from '../../lib/images';

/**
 * Fiche d'un titre.
 *
 * La page s'ouvre sur une **affiche animée**, pas sur un lecteur : image en
 * plein cadre, titre en lettrage, un seul bouton doré. La lecture prend la
 * scène quand on la demande, comme chez les grands services. Deux raisons,
 * au-delà du style : une affiche parle du film là où un lecteur à l'arrêt ne
 * parle que de lui-même, et le flux vidéo n'est plus ouvert par une simple
 * consultation de fiche.
 *
 * Le reste — favoris, vus, playlists, épisodes, distribution, rebond — est
 * inchangé : seul l'agencement bouge.
 */
export default function TitleSheet({
  movie,
  user,
  allMovies,
  watchlist,
  onToggleWatchlist,
  onSelectMovie,
  onWatchedStatusChange,
  onPlayingStatusChange,
  onClose,
}) {
  const catalog = useCatalog();
  const [synopsisOpen, setSynopsisOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [barSolid, setBarSolid] = useState(false);
  const scrollRef = useRef(null);
  const topRef = useRef(null); // héros ou scène de lecture, selon l'état

  /* Le fond ne défile pas derrière la fiche, et Échap referme. */
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  /* Changer de titre remet la fiche à zéro : sans ça, on garde le lecteur
     du titre précédent et un synopsis déjà déplié. */
  useEffect(() => {
    setPlaying(false);
    setSynopsisOpen(false);
    setBarSolid(false);
    scrollRef.current?.scrollTo({ top: 0 });
  }, [movie?.id]);

  /* La barre de tête ne prend son fond qu'une fois le héros sorti du champ.
     IntersectionObserver plutôt qu'un écouteur de défilement : le navigateur
     fait le calcul, et rien ne tourne pendant qu'on ne bouge pas. */
  useEffect(() => {
    const top = topRef.current;
    if (!top) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setBarSolid(!entry.isIntersecting),
      { root: scrollRef.current, threshold: 0, rootMargin: '-72px 0px 0px 0px' }
    );
    observer.observe(top);
    return () => observer.disconnect();
  }, [playing, movie?.id]);

  /* Les sections se lèvent à l'approche plutôt que d'être toutes là d'emblée.
     `.reveal` neutralise l'effet sous `prefers-reduced-motion`. */
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return undefined;

    const targets = root.querySelectorAll('.reveal:not(.is-in)');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { root, rootMargin: '0px 0px -8% 0px' }
    );

    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, [playing, movie?.id, synopsisOpen]);

  const isSeries = movie?.type === 'serie';

  const similar = useMemo(
    () => (catalog ? similarTo(catalog.titles, movie, 14) : []),
    [catalog, movie]
  );

  /* Le lecteur lit `shouldAutoPlay` à `canplay` et démarre seul — mais il
     saute alors la fenêtre de reprise et repart du début. On ne pose donc le
     drapeau que s'il n'y a rien à reprendre : sinon on laisse le lecteur
     proposer « Reprendre / Recommencer », qui est le bon geste. */
  const startPlayback = useCallback(() => {
    const started = catalog?.progressOf?.(movie) || 0;
    try {
      if (started > 0) sessionStorage.removeItem('shouldAutoPlay');
      else sessionStorage.setItem('shouldAutoPlay', 'true');
    } catch {
      /* navigation privée : le lecteur s'ouvrira sans démarrage automatique */
    }
    setPlaying(true);
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [catalog, movie]);

  if (!movie) return null;

  const releaseDate = movie.releaseDate
    ? new Date(movie.releaseDate).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  const overview = movie.episodeOverview || movie.overview || '';
  const overviewIsLong = overview.length > 320;
  const heading = movie.episodeTitle || movie.title;

  const facts = [
    Number(movie.rating) > 0 && (
      <span key="rating" className="score">
        <Star aria-hidden="true" />
        {Number(movie.rating).toFixed(1)}
      </span>
    ),
    movie.year && (
      <span key="year" className="inline-flex items-center gap-1.5">
        <Calendar className="w-3.5 h-3.5" aria-hidden="true" />
        {movie.year}
      </span>
    ),
    movie.duration && (
      <span key="duration" className="inline-flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5" aria-hidden="true" />
        {movie.duration}
      </span>
    ),
    movie.countries?.length > 0 && (
      <span key="countries" className="inline-flex items-center gap-1.5">
        <Globe className="w-3.5 h-3.5" aria-hidden="true" />
        {movie.countries.join(', ')}
      </span>
    ),
  ].filter(Boolean);

  return (
    <div className="titlesheet" role="dialog" aria-modal="true" aria-label={movie.title}>
      <div className={`titlesheet-bar${barSolid ? ' is-solid' : ''}`}>
        <button
          type="button"
          className="titlesheet-close"
          onClick={onClose}
          aria-label="Fermer la fiche"
        >
          <ArrowLeft aria-hidden="true" />
        </button>

        <p className="titlesheet-bar-title">{heading}</p>

        {!playing && (
          <button
            type="button"
            className="btn btn-gold btn-sm titlesheet-bar-play"
            onClick={startPlayback}
          >
            <Play aria-hidden="true" />
            Lecture
          </button>
        )}
      </div>

      <div className="titlesheet-scroll" ref={scrollRef}>
        {playing ? (
          <div className="titlesheet-stage" ref={topRef}>
            <VideoPlayer
              key={movie.id}
              movie={movie}
              user={user}
              allMovies={allMovies}
              onSelectMovie={onSelectMovie}
              onWatchedStatusChange={onWatchedStatusChange}
              onPlayingStatusChange={onPlayingStatusChange}
            />
          </div>
        ) : (
          <header className="titlesheet-hero" ref={topRef}>
            {movie.backdrop && (
              <div className="titlesheet-hero-art" aria-hidden="true">
                <img src={backdropSrc(movie.backdrop)} alt="" decoding="async" />
              </div>
            )}
            <div className="titlesheet-hero-veil" aria-hidden="true" />

            <div className="titlesheet-hero-body">
              <div className="titlesheet-hero-inner">
                {isSeries && movie.season != null && (
                  <p className="eyebrow eyebrow-gold mb-2">
                    Saison {movie.season} · Épisode {movie.episode}
                  </p>
                )}

                <h1 className="hero-title">{heading}</h1>

                {isSeries && movie.episodeTitle && (
                  <p className="titlesheet-hero-serie">{movie.title}</p>
                )}

                {facts.length > 0 && (
                  <div className="hero-facts">
                    {facts.map((fact, index) => (
                      <React.Fragment key={fact.key}>
                        {index > 0 && <span className="dot" aria-hidden="true" />}
                        {fact}
                      </React.Fragment>
                    ))}
                  </div>
                )}

                {overview && <p className="hero-pitch clamp-3">{overview}</p>}

                <div className="titlesheet-hero-actions">
                  <button type="button" className="btn btn-gold btn-lg" onClick={startPlayback}>
                    <Play aria-hidden="true" />
                    Lecture
                  </button>
                  <WatchlistButton
                    movieId={movie.id}
                    isInWatchlist={watchlist.includes(movie.id)}
                    onToggle={onToggleWatchlist}
                    size="large"
                  />
                  <WatchedButton movie={movie} user={user} onStatusChange={onWatchedStatusChange} />
                  <AddToPlaylistButton movie={movie} />
                </div>
              </div>
            </div>
          </header>
        )}

        <div className="titlesheet-body">
          <div className="shell stack" style={{ gap: 'clamp(1.75rem, 4vw, 2.75rem)' }}>
            {/* En lecture, le titre a quitté l'affiche : il lui faut un rappel. */}
            {playing && (
              <header className="stack gap-3 reveal">
                <div>
                  {isSeries && movie.season != null && (
                    <p className="eyebrow eyebrow-gold mb-2">
                      Saison {movie.season} · Épisode {movie.episode}
                    </p>
                  )}
                  <h1 className="display-md">{heading}</h1>
                </div>
                {facts.length > 0 && <div className="hero-facts">{facts}</div>}
              </header>
            )}

            {movie.genres?.length > 0 && (
              <div className="flex flex-wrap gap-2 reveal">
                {movie.genres.map((genre) => (
                  <span key={genre} className="tag">
                    {genre}
                  </span>
                ))}
              </div>
            )}

            <div className="titlesheet-grid reveal delay-100">
              <div className="stack gap-8 min-w-0">
                {overview && (
                  <section className="titlesheet-section">
                    <h2 className="eyebrow">Synopsis</h2>
                    <p
                      className={`text-[var(--fs-md)] leading-relaxed text-[var(--t-mid)]${
                        synopsisOpen || !overviewIsLong ? '' : ' clamp-4'
                      }`}
                    >
                      {overview}
                    </p>
                    {overviewIsLong && (
                      <button
                        type="button"
                        className="figure-bio-more"
                        onClick={() => setSynopsisOpen((open) => !open)}
                      >
                        {synopsisOpen ? 'Réduire' : 'Lire la suite'}
                      </button>
                    )}
                  </section>
                )}

                <CastRail
                  tmdbId={movie.tmdbId}
                  type={movie.type}
                  fallbackCast={movie.cast || []}
                  director={movie.director}
                />
              </div>

              <aside className="stack gap-3">
                {movie.director && (
                  <div className="panel panel-pad">
                    <p className="eyebrow mb-1">Réalisation</p>
                    <p className="text-[var(--t-hi)] font-medium">{movie.director}</p>
                  </div>
                )}
                {releaseDate && (
                  <div className="panel panel-pad">
                    <p className="eyebrow mb-1">Sortie</p>
                    <p className="text-[var(--t-hi)] font-medium">{releaseDate}</p>
                  </div>
                )}
                {movie.originalTitle && movie.originalTitle !== movie.title && (
                  <div className="panel panel-pad">
                    <p className="eyebrow mb-1">Titre original</p>
                    <p className="text-[var(--t-hi)] font-medium">{movie.originalTitle}</p>
                  </div>
                )}
              </aside>
            </div>

            <div className="reveal delay-150">
              <EpisodesList
                currentMovie={movie}
                allMovies={allMovies}
                onSelectEpisode={onSelectMovie}
              />
            </div>
          </div>

          {similar.length > 0 && (
            <div className="pt-2 reveal delay-200">
              <Rail title="Dans le même esprit" icon={<Users aria-hidden="true" />}>
                {similar.map((title, index) => (
                  <Poster
                    key={title.id}
                    index={index}
                    title={title}
                    onOpen={(next) => {
                      onClose();
                      catalog?.onOpenTitle(next);
                    }}
                    inList={catalog?.isInList(title)}
                    watched={catalog?.isWatched(title)}
                  />
                ))}
              </Rail>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
